import type { ReadingSlide } from "../ui/StartReader";
import { AppChip, C, Frame, MONO } from "../ui/readingArt";
import { tx, txv } from "../lib/tx";

/**
 * "Read while you wait" summary for the invoice-that-changed start screen.
 * Three short slides (what/who and why a human is the control, red flags,
 * how to verify) shown in the reader while the 3D room downloads. Content only;
 * the reader UI lives in ui/StartReader.tsx.
 */

/* ------------------------------------------------------------------ */
/* 1. invoice with a changed bank-account line                         */
/* ------------------------------------------------------------------ */
function InvoiceChangedArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label={tx("Example: supplier invoice")} aria={tx("A supplier invoice where the bank account line has changed and is highlighted")} height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        {tx("Invoice AT-4021")}
      </text>
      <rect x="262" y="38" width="82" height="22" rx="11" fill={C.panel2} stroke={C.line} />
      <text x="303" y="53" fontSize="10.5" fontWeight="700" fill={C.muted} textAnchor="middle">
        {tx("PO BM-2187")}
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />

      <text x="16" y="90" fontSize="10.5" fill={C.muted}>
        {tx("Supplier")}
      </text>
      <text x="344" y="90" fontSize="12" fill={C.text} textAnchor="end">
        {tx("Aurelia Fine Art Transport AG")}
      </text>
      <text x="16" y="110" fontSize="10.5" fill={C.muted}>
        {tx("For")}
      </text>
      <text x="344" y="110" fontSize="12" fill={C.text} textAnchor="end">
        {tx("Transport and insurance")}
      </text>
      <text x="16" y="130" fontSize="10.5" fill={C.muted}>
        {tx("Amount due")}
      </text>
      <text x="344" y="130" fontSize="12" fontWeight="700" fill={C.text} textAnchor="end">
        {tx("CHF 48,600.00")}
      </text>
      <line x1="16" y1="142.5" x2="344" y2="142.5" stroke={C.line} strokeOpacity="0.6" />

      <text x="16" y="160" fontSize="10.5" fill={C.muted}>
        {tx("Pay to")}
      </text>
      <text x="344" y="160" fontSize="10.5" fill={C.muted} textAnchor="end">
        {tx("(bank account)")}
      </text>

      <rect x="16.5" y="168.5" width="327" height="30" rx="8" fill={C.panel2} stroke={C.line} />
      <text x="29" y="187" fontSize="11" fill={C.muted}>
        {tx("Approved")}
      </text>
      <text x="100" y="187" fontSize="12" fontFamily={MONO} fill={C.muted}>
        {tx("CH")}{"\u2022\u2022"} {"\u2022\u2022\u2022\u2022"} 1942
      </text>
      <line x1="98" y1="183.5" x2="214" y2="183.5" stroke={C.muted} strokeWidth="1.2" />
      <text x="330" y="187" fontSize="10.5" fill={C.muted} textAnchor="end">
        {tx("on file")}
      </text>

      <rect className="sr-anim-pulse" x="16.5" y="206.5" width="327" height="34" rx="8" fill={C.red} fillOpacity="0.1" stroke={C.red} strokeOpacity="0.55" />
      <rect x="16" y="211" width="3" height="25" rx="1.5" fill={C.red} />
      <text x="29" y="227" fontSize="11" fontWeight="700" fill={C.red}>
        {tx("Revised")}
      </text>
      <text x="100" y="227" fontSize="12" fontFamily={MONO} fontWeight="700" fill={C.red}>
        {tx("CH")}{"\u2022\u2022"} {"\u2022\u2022\u2022\u2022"} 7785
      </text>
      <circle className="sr-anim-ring" cx="320" cy="223.5" r="6" fill="none" stroke={C.red} strokeWidth="1.5" />
      <circle cx="320" cy="223.5" r="2" fill={C.red} />

      <rect x="16.5" y="250.5" width="327" height="30" rx="8" fill={C.amber} fillOpacity="0.1" stroke={C.amber} strokeOpacity="0.4" />
      <text x="29" y="269.5" fontSize="12" fontWeight="600" fill={C.amber}>
        {tx("Same number, same amount. Only the account moved.")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. email thread with a subtly wrong reply-to domain                 */
/* ------------------------------------------------------------------ */
function ReplyToArt(): JSX.Element {
  const H = 298;
  return (
    <Frame label={tx("Example: email thread")} aria={tx("An email in a familiar thread where the reply-to domain differs by one character from the sender domain")} height={H}>
      <clipPath id="itc-replyto-clip">
        <rect x="84" y="97" width="248" height="22" rx="4" />
      </clipPath>

      <circle cx="28" cy="49" r="12" fill={C.mint} fillOpacity="0.14" />
      <text x="28" y="53" fontSize="11" fontWeight="700" fill={C.mint} textAnchor="middle">
        {tx("LV")}
      </text>
      <text x="48" y="46" fontSize="13" fontWeight="700" fill={C.text}>
        {tx("Lena Vogt")}
      </text>
      <text x="48" y="60" fontSize="10.5" fill={C.muted}>
        {tx("Re: Invoice AT-4021")} {"\u00b7"} 16:52
      </text>
      <rect x="276" y="38" width="68" height="22" rx="11" fill={C.panel2} stroke={C.line} />
      <text x="310" y="53" fontSize="10.5" fontWeight="700" fill={C.soft} textAnchor="middle">
        {tx("Same thread")}
      </text>
      <line x1="16" y1="72.5" x2="344" y2="72.5" stroke={C.line} />

      <rect x="16.5" y="80.5" width="327" height="46" rx="8" fill={C.code} stroke={C.line} />
      <text x="28" y="96" fontSize="10.5" fill={C.muted}>
        {tx("From")}
      </text>
      <text x="88" y="96" fontSize="11" fontFamily={MONO} fill={C.soft}>
        l.vogt@aurelia-transport.ch
      </text>
      <text x="28" y="115" fontSize="10.5" fill={C.muted}>
        {tx("Reply-to")}
      </text>
      <text x="88" y="115" fontSize="11" fontFamily={MONO} fontWeight="700" fill={C.red}>
        l.vogt@aurelia-transp0rt.ch
      </text>
      <g clipPath="url(#itc-replyto-clip)">
        <rect className="sr-anim-sweep" x="84" y="97" width="30" height="22" fill={C.red} fillOpacity="0.22" />
      </g>
      <line x1="248" y1="119" x2="262" y2="119" stroke={C.red} strokeWidth="1.5" strokeLinecap="round" />

      <rect x="16.5" y="136.5" width="230" height="30" rx="9" fill={C.panel2} stroke={C.line} />
      <text x="29" y="155" fontSize="11.5" fill={C.muted}>
        {tx("Invoice AT-4021 attached, thank you.")}
      </text>
      <rect x="86.5" y="174.5" width="257" height="64" rx="9" fill={C.panel2} stroke={C.line} />
      <text x="99" y="192" fontSize="11.5" fill={C.text}>
        {tx("Our bank has changed. Please pay")}
      </text>
      <text x="99" y="208" fontSize="11.5" fill={C.text}>
        {tx("to the new account on the revised")}
      </text>
      <text x="99" y="224" fontSize="11.5" fill={C.text}>
        {tx("invoice, before 18:00 today.")}
      </text>
      <rect className="sr-anim-blink" x="231" y="213" width="1.5" height="14" rx="0.75" fill={C.mint} />

      <rect x="16.5" y="248.5" width="327" height="34" rx="8" fill={C.red} fillOpacity="0.08" stroke={C.red} strokeOpacity="0.4" />
      <path d="M33 258 l6.5 11 h-13 z" fill="none" stroke={C.red} strokeWidth="1.5" strokeLinejoin="round" />
      <line x1="33" y1="261.6" x2="33" y2="265" stroke={C.red} strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="33" cy="267.2" r="0.9" fill={C.red} />
      <text x="48" y="269" fontSize="11.5" fontWeight="700" fill={C.red}>
        {tx("Replies would go to a lookalike domain")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. verify on a known number                                         */
/* ------------------------------------------------------------------ */
function VerifyArt(): JSX.Element {
  const H = 298;
  const rows: { y: number; title: string; sub: string; ok: boolean }[] = [
    { y: 36, title: "Reply in the thread", sub: "Answered by whoever sent it", ok: false },
    { y: 82, title: "Number on the new invoice", sub: "The attacker wrote it", ok: false },
    { y: 128, title: "Number already on file", sub: "Set before the request", ok: true },
  ];
  return (
    <Frame label={tx("Which channel to trust")} aria={tx("Three ways to check a bank-detail change: replying in the thread and the number on the new invoice are untrusted, the number already on file is trusted")} height={H}>
      {rows.map((r) => {
        const col = r.ok ? C.mint : C.red;
        return (
          <g key={r.title}>
            <rect x="16.5" y={r.y + 0.5} width="327" height="39" rx="10" fill={r.ok ? C.mint : C.panel2} fillOpacity={r.ok ? 0.1 : 1} stroke={r.ok ? C.mint : C.line} strokeOpacity={r.ok ? 0.5 : 1} />
            <circle cx="38" cy={r.y + 20} r="12" fill={col} fillOpacity="0.14" />
            {r.ok ? (
              <polyline points={`32.5,${r.y + 20} 36.5,${r.y + 24} 44,${r.y + 15.5}`} fill="none" stroke={col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            ) : (
              <g stroke={col} strokeWidth="2" strokeLinecap="round">
                <line x1="33.5" y1={r.y + 15.5} x2="42.5" y2={r.y + 24.5} />
                <line x1="42.5" y1={r.y + 15.5} x2="33.5" y2={r.y + 24.5} />
              </g>
            )}
            <text x="60" y={r.y + 18} fontSize="12.5" fontWeight="700" fill={C.text}>
              {txv(r.title)}
            </text>
            <text x="60" y={r.y + 32} fontSize="10.5" fill={C.muted}>
              {txv(r.sub)}
            </text>
            <text x="332" y={r.y + 25} fontSize="10.5" fontWeight="700" fill={col} textAnchor="end">
              {r.ok ? tx("TRUSTED") : tx("NOT SAFE")}
            </text>
          </g>
        );
      })}

      <rect x="16.5" y="182.5" width="327" height="100" rx="12" fill={C.panel2} stroke={C.mint} strokeOpacity="0.45" />
      <g className="sr-anim-float">
        <circle cx="52" cy="232" r="22" fill={C.mint} fillOpacity="0.14" />
        <circle className="sr-anim-ring" cx="52" cy="232" r="22" fill="none" stroke={C.mint} strokeWidth="1.5" />
        <path
          d="M44 224 q0 -3 3 -3 h3 l2.5 6 -3 2 q2 4 6 6 l2 -3 6 2.5 v3 q0 3 -3 3 q-18 -1 -20.5 -17.5 z"
          fill="none"
          stroke={C.mint}
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <text x="88" y="214" fontSize="10.5" fontWeight="700" fill={C.mint} letterSpacing="0.6">
        {tx("BEFORE ANY MONEY MOVES")}
      </text>
      <text x="88" y="234" fontSize="13.5" fontWeight="700" fill={C.text}>
        {tx("Verify the change on")}
      </text>
      <text x="88" y="252" fontSize="13.5" fontWeight="700" fill={C.text}>
        {tx("a known number")}
      </text>
      <text x="88" y="270" fontSize="10.5" fill={C.muted}>
        {tx("Then hold the payment until it is confirmed.")}
      </text>
    </Frame>
  );
}

export const INVOICE_THAT_CHANGED_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "The invoice is real. The bank account is not.",
    illustration: InvoiceChangedArt,
    caption: "One changed line is all it takes.",
    sections: [
      {
        h: "What this is about",
        body: "It is opening day, and a trusted supplier asks you to pay a genuine invoice into a new bank account before 18:00. You check the invoice, test the request, and decide whether the money moves \u2014 and where it goes.",
      },
      {
        h: "Who it\u2019s for",
        body: "Finance and accounts payable, and anyone who approves, processes or forwards a supplier payment. No finance qualification needed.",
      },
      {
        h: "Why your judgement matters",
        body: "The invoice number, the amount and the supplier all match, so nothing looks wrong. Software cannot tell a real account change from a fake one. Once the payment leaves, it is rarely coming back. You are the last check.",
      },
    ],
  },
  {
    tab: "What to watch for",
    headline: "A familiar thread can still be a trap.",
    illustration: ReplyToArt,
    caption: "Look at where a reply would actually go.",
    sections: [
      {
        h: "A bank-detail change",
        body: "A new account on a supplier you already pay is the core red flag. Real changes happen, but they are rare and always worth proving.",
      },
      {
        h: "A trusted conversation",
        body: "Attackers break into or copy a real mailbox and reply inside the existing thread. The tone, the history and the attachment all look right.",
      },
      {
        h: "A subtly wrong address",
        body: "One swapped letter in a sender or reply-to domain is easy to miss. Check the full address, not the display name.",
      },
      {
        h: "A deadline and pressure",
        body: "\u201cPay before 18:00\u201d is there to stop you checking. Urgency is a reason to slow down.",
      },
    ],
  },
  {
    tab: "How to verify",
    headline: "One call on a number you already trust.",
    illustration: VerifyArt,
    caption: "The channel matters more than the effort.",
    sections: [
      {
        h: "Payment redirection",
        body: "Fraud that sends a genuine payment to an attacker\u2019s account by changing the bank details. Also called supplier or invoice fraud.",
      },
      {
        h: "Business email compromise",
        body: "An attacker uses, or imitates, a real business mailbox to trick staff into paying or sharing information.",
      },
      {
        h: "Independent verification",
        body: "Checking a request through a channel the sender does not control. A reply in the same thread, or a number on the new invoice, is not independent.",
      },
      {
        h: "Number on file",
        body: "A contact you saved before the request arrived. Call it, ask them to confirm the change, and hold the payment until they do.",
      },
      {
        h: "Report it",
        body: "If the change is not confirmed, keep the payment on hold and tell your security or finance team straight away.",
      },
    ],
  },
];
